import React, { useRef, useState, useEffect } from "react";
import "./TestimonialsSection.css";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";

export interface Testimonial {
  id: number | string;
  name: string;
  date?: string;
  content: string;
  rating: number;
  category?: string;
  avatar?: string;
}

interface TestimonialsSectionProps {
  testimonials: Testimonial[];
  title?: string;
  subtitle?: string;
  badgeText?: string;
}

const TestimonialsSection = ({
  testimonials,
  title = "Depoimentos dos nossos clientes",
  subtitle = "Veja o que nossos clientes dizem sobre o trabalho da Monk Studio e os resultados que entregamos.",
  badgeText = "AVALIAÇÕES 5 ESTRELAS NO GOOGLE"
}: TestimonialsSectionProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      if (scrollRef.current) {
        const { scrollLeft, clientWidth, scrollWidth } = scrollRef.current;
        let nextScroll = scrollLeft + clientWidth;
        
        if (nextScroll >= scrollWidth - 10) {
          nextScroll = 0;
        }
        
        scrollRef.current.scrollTo({ left: nextScroll, behavior: 'smooth' });
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [isPaused]);

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const scrollTo = direction === 'left' ? scrollLeft - clientWidth : scrollLeft + clientWidth;
      scrollRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleScrollEvent = () => {
      if (scrollRef.current) {
        const { scrollLeft, clientWidth } = scrollRef.current;
        const newIndex = Math.round(scrollLeft / clientWidth);
        setActiveIndex(newIndex);
      }
    };

    const currentRef = scrollRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScrollEvent);
    }
    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScrollEvent);
      }
    };
  }, []);

  return (
    <section id="avaliacoes" className="bg-[#0a0a0a] py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-[10px] font-bold tracking-[0.2em] text-white/60 uppercase">
            <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
            {badgeText}
          </div>
          
          <h2 className="text-4xl md:text-6xl font-black text-white leading-[1.1] tracking-tight max-w-3xl">
            Depoimentos dos <br />
            nossos <span className="bg-gradient-to-r from-orange-400 via-orange-500 to-pink-500 bg-clip-text text-transparent">clientes</span>
          </h2>
          
          <p className="text-gray-400 text-lg md:text-xl max-w-2xl font-medium">
            {subtitle}
          </p>

          <div className="pt-8 flex flex-col items-center gap-2">
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" 
              alt="Google" 
              className="h-8 opacity-90"
            />
            <div className="flex gap-1 text-yellow-500">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-current" />
              ))}
            </div>
            <p className="text-gray-500 text-sm font-medium">5.0 de 5 estrelas</p>
          </div>
        </div>

        {/* Carousel Container */}
        <div className="relative group">
          {/* Navigation Arrows */}
          <button 
            onClick={() => handleScroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 z-20 w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all opacity-0 group-hover:opacity-100 hidden md:flex"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <button 
            onClick={() => handleScroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-6 z-20 w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all opacity-0 group-hover:opacity-100 hidden md:flex"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Cards Scrollable Area */}
          <div 
            ref={scrollRef}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-none pb-4 px-1"
          >
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={testimonial.id}
                className="min-w-full md:min-w-[calc(33.333%-16px)] snap-start"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`h-full bg-[#141414] rounded-[32px] p-6 md:p-8 border ${idx === activeIndex ? 'border-orange-500/50' : 'border-white/5'} flex flex-col gap-4 relative transition-all duration-300 hover:border-white/20`}>
                  <div className="flex justify-between items-start">
                    <div className="flex gap-0.5 text-yellow-500">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-3.5 h-3.5 fill-current ${i >= testimonial.rating ? 'opacity-30' : ''}`} />
                      ))}
                    </div>
                    <img 
                      src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" 
                      alt="G" 
                      className="w-4 h-4 opacity-40 grayscale"
                    />
                  </div>

                  <div className="space-y-0.5">
                    <h4 className="text-white font-bold text-base md:text-lg">{testimonial.name}</h4>
                  </div>

                  <p className="text-gray-400 leading-relaxed font-medium text-sm md:text-base line-clamp-6">
                    {testimonial.content}
                  </p>

                  {testimonial.category && (
                    <div className="mt-auto flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 w-fit">
                      <div className="w-1 h-1 rounded-full bg-gray-600" />
                      <span className="text-gray-400 text-[10px] font-bold uppercase tracking-wider">{testimonial.category}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Indicators */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, i) => (
              <div 
                key={i} 
                className={`h-1.5 rounded-full transition-all duration-300 ${activeIndex === i ? 'w-8 bg-orange-500' : 'w-1.5 bg-white/20'}`} 
              />
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="mt-16 flex justify-center">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold text-lg shadow-[0_0_40px_rgba(249,115,22,0.2)] hover:shadow-[0_0_60px_rgba(249,115,22,0.4)] transition-all"
          >
            Ver mais avaliações no Google
            <ExternalLink className="w-5 h-5" />
          </motion.button>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;